import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import { EnhancedSelect, EnhancedSelectOptionData } from "../utils/enhanced_select"

export default class BrandSelectionController extends Controller {
  static targets = [
    "brandSelect",
    "amountSection",
    "amountButtons",
    "amountButton",
    "brandCodeField",
    "nameField",
    "amountField",
    "imageUrlField"
  ]

  static values = {
    context: String,
    amountButtonsUrl: String
  }

  declare readonly brandSelectTarget: HTMLSelectElement
  declare readonly amountSectionTarget: HTMLElement
  declare readonly amountButtonsTarget: HTMLElement
  declare readonly amountButtonTargets: HTMLButtonElement[]
  declare readonly brandCodeFieldTarget: HTMLInputElement
  declare readonly nameFieldTarget: HTMLInputElement
  declare readonly amountFieldTarget: HTMLInputElement
  declare readonly imageUrlFieldTarget: HTMLInputElement

  declare contextValue: string
  declare amountButtonsUrlValue: string

  private brandSelect: EnhancedSelect | null = null
  private selectedAmountButton: HTMLButtonElement | null = null
  private managingSubmit = true

  disableSubmitManagement(): void {
    this.managingSubmit = false
  }

  enableSubmitManagement(): void {
    this.managingSubmit = true
    this.updateSubmitState()
  }

  setSubmitDisabled(disabled: boolean): void {
    const btn = this.submitButton
    if (btn) btn.disabled = disabled
  }

  brandSelectTargetConnected(): void {
    this.initSelect()
    this.updateSubmitState()
  }

  brandSelectTargetDisconnected(): void {
    if (this.brandSelect) {
      this.brandSelect.destroy()
      this.brandSelect = null
    }
  }

  amountFieldTargetConnected(): void {
    this.updateSubmitState()
  }

  disconnect(): void {
    if (this.brandSelect) {
      this.brandSelect.destroy()
      this.brandSelect = null
    }
  }

  private initSelect(): void {
    this.brandSelect = new EnhancedSelect(this.brandSelectTarget, {
      placeholder: "Search gift card brands...",
      allowRemove: false,
      maxItems: 1,
      closeOnSelect: true,
      onItemAdd: (value: string) => this.brandChanged(value),
      onClear: () => this.brandCleared(),
      render: {
        option: (data, escape) => this.renderBrandOption(data, escape),
        item: (data, escape) => this.renderBrandOption(data, escape)
      }
    })
  }

  private renderBrandOption(data: EnhancedSelectOptionData, escape: (str: string) => string): string {
    const option = data.$option
    const imageUrl = option?.dataset?.imageUrl

    if (!imageUrl) {
      return `<div>${escape(data.text || "")}</div>`
    }

    return `
      <div class="d-flex align-items-center">
        <img src="${escape(imageUrl)}" class="brand-select-image rounded me-2" />
        <span>${escape(data.text || "")}</span>
      </div>
    `
  }

  brandChanged(brandCode?: string): void {
    // Get brand code from parameter (tom-select callback) or fall back to selectedOptions
    // Note: When called from onItemAdd, selectedOptions may not be updated yet
    if (!brandCode) {
      const selectedOption = this.brandSelectTarget.selectedOptions[0]
      brandCode = selectedOption?.value
    }

    if (!brandCode) {
      this.brandCleared()
      return
    }

    // Skip fetch if brand hasn't actually changed (prevents race condition)
    if (this.brandCodeFieldTarget.value === brandCode) {
      return
    }

    this.amountSectionTarget.classList.remove("d-none")
    this.selectedAmountButton = null

    const url = new URL(this.amountButtonsUrlValue, window.location.origin)
    url.searchParams.set("brand_code", brandCode)
    url.searchParams.set("context", this.contextValue)

    Turbo.visit(url.toString(), { frame: "amount_buttons_frame" })

    this.updateSubmitState()
  }

  brandCleared(): void {
    this.amountSectionTarget.classList.add("d-none")
    this.selectedAmountButton = null

    const url = new URL(this.amountButtonsUrlValue, window.location.origin)
    url.searchParams.set("context", this.contextValue)

    Turbo.visit(url.toString(), { frame: "amount_buttons_frame" })

    this.updateSubmitState()
  }

  selectAmount(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement
    const amount = button.dataset.amount || ""

    this.amountFieldTarget.value = amount

    // Clear all amount button selections (including server-rendered pre-selected buttons)
    this.amountButtonTargets.forEach((btn) => {
      btn.classList.remove("btn-primary")
      btn.classList.add("btn-primary-subtle")
    })

    button.classList.remove("btn-primary-subtle")
    button.classList.add("btn-primary")
    this.selectedAmountButton = button

    this.updateSubmitState()
  }

  private get submitButton(): HTMLButtonElement | null {
    return this.element.querySelector('[type="submit"]')
  }

  private updateSubmitState(): void {
    if (!this.submitButton || !this.managingSubmit) {
      return
    }

    const brandCode = this.brandCodeFieldTarget.value
    const amount = this.amountFieldTarget.value
    const hasSelection = brandCode !== "" && amount !== ""

    this.submitButton.disabled = !hasSelection
  }

  reset(): void {
    // Restore the original default selection from the HTML
    const defaultOption = this.brandSelectTarget.querySelector(
      "option[selected]"
    ) as HTMLOptionElement | null
    const defaultValue = defaultOption?.value || ""

    if (this.brandSelect) {
      this.brandSelect.clear(true)
      if (defaultValue) {
        this.brandSelect.setValue(defaultValue, true)
      }
    }

    // Reset amount section visibility and button state
    if (defaultValue) {
      this.amountSectionTarget.classList.remove("d-none")
    } else {
      this.amountSectionTarget.classList.add("d-none")
    }
    this.selectedAmountButton = null
  }
}
